import { create } from 'zustand';
import type { ConnectionState, ConnectionStatus } from '@/types/endpoint';

const SERVICE_TAG = '[ACTIVE_CONNECTIONS]';
const MAX_CONNECTIONS = 3;

interface ActiveConnectionsState {
  connections: Map<string, ConnectionState>;

  setStatus: (endpointId: string, status: ConnectionStatus, error?: string) => void;
  setSessionData: (endpointId: string, sessionData: any) => void;
  getStatus: (endpointId: string) => ConnectionStatus;
  getConnectionState: (endpointId: string) => ConnectionState | undefined;
  getActiveCount: () => number;
  canConnect: () => boolean;
  clearConnection: (endpointId: string) => void;
  clearAll: () => void;
}

/**
 * Ephemeral store for active WebSocket connection states
 * NO persistence - connection state is lost on refresh (intentional)
 */
export const useActiveConnectionsStore = create<ActiveConnectionsState>((set, get) => ({
  connections: new Map(),

  setStatus: (endpointId, status, error) => {
    console.info(`${SERVICE_TAG} Setting connection status`, {
      endpointId,
      status,
      hasError: !!error
    });

    set((state) => {
      const newConnections = new Map(state.connections);
      const existing = newConnections.get(endpointId);

      const updated: ConnectionState = {
        status,
        connectedAt: status === 'connected' ? new Date() : existing?.connectedAt,
        sessionData: existing?.sessionData,
        lastError: error || (status === 'error' ? existing?.lastError : undefined)
      };

      newConnections.set(endpointId, updated);

      return { connections: newConnections };
    });
  },

  setSessionData: (endpointId, sessionData) => {
    console.info(`${SERVICE_TAG} Setting session data`, { endpointId });

    set((state) => {
      const newConnections = new Map(state.connections);
      const existing = newConnections.get(endpointId);

      if (existing) {
        newConnections.set(endpointId, {
          ...existing,
          sessionData
        });
      }

      return { connections: newConnections };
    });
  },

  getStatus: (endpointId) => {
    const connection = get().connections.get(endpointId);
    return connection?.status || 'disconnected';
  },

  getConnectionState: (endpointId) => {
    return get().connections.get(endpointId);
  },

  getActiveCount: () => {
    const connections = get().connections;
    let count = 0;

    connections.forEach((state) => {
      if (state.status === 'connected' || state.status === 'connecting') {
        count++;
      }
    });

    return count;
  },

  canConnect: () => {
    const activeCount = get().getActiveCount();
    const canConnect = activeCount < MAX_CONNECTIONS;

    if (!canConnect) {
      console.warn(`${SERVICE_TAG} Cannot connect, max connections reached`, {
        activeCount,
        maxConnections: MAX_CONNECTIONS
      });
    }

    return canConnect;
  },

  clearConnection: (endpointId) => {
    console.info(`${SERVICE_TAG} Clearing connection`, { endpointId });

    set((state) => {
      const newConnections = new Map(state.connections);
      newConnections.delete(endpointId);
      return { connections: newConnections };
    });
  },

  clearAll: () => {
    console.info(`${SERVICE_TAG} Clearing all connections`);
    set({ connections: new Map() });
  }
}));

// Expose store for E2E testing (dev mode only)
if (import.meta.env.DEV) {
  (window as any).__activeConnectionsStore = useActiveConnectionsStore;
}
